import { useEffect, useState, useCallback } from 'react';
import { useAppSelector } from '../store';
import { useActiveEvent } from '../contexts/EventContext';
import { API_BASE_URL } from '../lib/utils';
import MapDrawingTools from '../components/map/MapDrawingTools';
import { useNavigate } from 'react-router-dom';
import type { Incident } from '../types';

export default function MapPage() {
  const { token } = useAppSelector((state) => state.auth);
  const { activeEventId } = useActiveEvent();
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState<Incident[]>([]);

  const fetchIncidents = useCallback(async () => {
    if (!token) return;
    try {
      const url = activeEventId
        ? `${API_BASE_URL}/incidents?eventId=${activeEventId}`
        : `${API_BASE_URL}/incidents`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setIncidents(data.incidents || data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
    }
  }, [token, activeEventId]);

  useEffect(() => {
    fetchIncidents();
    const interval = setInterval(fetchIncidents, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchIncidents]);

  const handleIncidentClick = (incident: Incident) => {
    // Open incident in parent window if this is a popup
    if (window.opener) {
      window.opener.postMessage({ type: 'NAVIGATE_TO_INCIDENT', incidentId: incident.id }, '*');
    } else {
      navigate(`/workspace/incidents/${incident.id}`);
    }
  };

  return (
    <div className="h-screen w-screen bg-background flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 bg-card border-b border-border px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
            <span className="text-primary font-bold text-sm">C2</span>
          </div>
          <div>
            <h1 className="text-sm font-bold text-foreground">Tactical Map</h1>
            <p className="text-xs text-muted-foreground">Real-time operational overview</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span>Live</span>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 min-h-0">
        <MapDrawingTools
          className="h-full"
          incidents={incidents}
          onIncidentClick={handleIncidentClick}
          isPanel={false}
        />
      </div>
    </div>
  );
}
